import React, { useCallback } from "react";
import { Overlay, Popover } from "react-bootstrap";
import { classNames } from "Utils/utils";
import {
  DEFAULT_MAX_WIDTH,
  DEFAULT_OFFSET,
  DEFAULT_Z_INDEX,
} from "./constants";
import {
  buildPopperConfig,
  getPopperContainer,
  getVariantClassName,
  getVariantStyles,
} from "./bootstrapPopoverUtils";
import "./floatingPopover.css";

const mergeRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (typeof ref === "function") {
      ref(node);
    } else if (ref && typeof ref === "object") {
      ref.current = node;
    }
  });
};

const FloatingPopoverPanel = ({
  open,
  referenceRef,
  variant = "default",
  placement = "top",
  maxWidth = DEFAULT_MAX_WIDTH,
  zIndex = DEFAULT_Z_INDEX,
  showArrow = true,
  offsetPx = DEFAULT_OFFSET,
  interactive = true,
  preventFlip = false,
  rootClose = false,
  onHide,
  className = "",
  contentClassName = "",
  onMouseEnter,
  onMouseLeave,
  children,
  panelRef,
}) => {
  const variantStyles = getVariantStyles(variant);
  const variantClass = getVariantClassName(variant);

  const setPanelRef = useCallback(
    (node) => {
      if (typeof panelRef === "function") {
        panelRef(node);
      } else if (panelRef) {
        panelRef.current = node;
      }
    },
    [panelRef]
  );

  if (!referenceRef) return null;

  return (
    <Overlay
      show={Boolean(open)}
      target={referenceRef}
      placement={placement}
      container={getPopperContainer}
      popperConfig={buildPopperConfig(offsetPx, { preventFlip })}
      transition={false}
      rootClose={rootClose}
      onHide={onHide}
    >
      {(overlayRenderProps) => {
        const {
          arrowProps,
          popper: _popper,
          show: _show,
          ...popoverOverlayProps
        } = overlayRenderProps;

        return (
          <Popover
            {...popoverOverlayProps}
            arrowProps={showArrow ? arrowProps : { style: { display: "none" } }}
            ref={mergeRefs(popoverOverlayProps.ref, setPanelRef)}
            id={`floating-popover-${variant}`}
            className={classNames(
              popoverOverlayProps.className,
              variantClass,
              !showArrow && "floating-popover--no-arrow",
              className
            )}
            style={{
              ...popoverOverlayProps.style,
              zIndex,
              maxWidth: `${maxWidth}px`,
              boxShadow: variantStyles.panelBoxShadow,
              pointerEvents: interactive ? "auto" : "none",
              "--fp-border": variantStyles.borderColor,
              "--fp-bg": variantStyles.arrowBackground,
            }}
            onMouseEnter={interactive ? onMouseEnter : undefined}
            onMouseLeave={interactive ? onMouseLeave : undefined}
          >
            <Popover.Content
              className={classNames(
                variantStyles.contentClassName,
                contentClassName
              )}
              onMouseEnter={interactive ? onMouseEnter : undefined}
              onMouseLeave={interactive ? onMouseLeave : undefined}
            >
              {children}
            </Popover.Content>
          </Popover>
        );
      }}
    </Overlay>
  );
};

export default FloatingPopoverPanel;
